import styles from '../layout/panel/Tour.module.css';
import arrowIcon from '../assets/icons/arrow.svg';
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Step } from "../types/Tour";
import filledClickIcon from '../assets/icons/clicked-cursor.png';
import clickIcon from '../assets/icons/cursor.png';
import Pill from "../components/peel/Pill";

type TourContextType = {
    trigger: (steps: Step[]) => void;
}

type TourContextProps = {
    children: ReactNode;
}

const TourContext = createContext<TourContextType | null>(null);

export function TourContextProvider({ children }: TourContextProps) {
    const [steps, setSteps] = useState<Step[] | null>(null);
    const [currentStep, setCurrentStep] = useState<number | null>(null);
    const [currentBoundingData, setCurrentBoundingData] = useState<DOMRect | null>(null);

    const [clickIconState, setClickIconState] = useState(false);
    const [clickBoundingData, setClickBoundingData] = useState<DOMRect | null>(null);

    const [currentElement, setCurrentElement] = useState<null | HTMLElement>(null);
    const [currentClickElement, setCurrentClickElement] = useState<null | HTMLElement>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const overlayRef = useRef<HTMLDivElement | null>(null);
    const clickTimerRef = useRef<number | null>(null);
    const stepsRef = useRef<null | Step[]>(null);

    const listenerFunction = useCallback((el: MouseEvent) => {
        setCurrentStep(pre => {
            const steps = stepsRef.current;
            if (!steps) return pre;

            const previousStepData = steps[pre || 0];
            const targetId = (el.target as HTMLElement).id;

            const condition = previousStepData.looseSelectionId
                ? 
                    targetId.includes(previousStepData.looseSelectionId)
                : 
                    previousStepData.specificClick 
                        ? targetId === previousStepData.clickSelector 
                        : targetId !== 'tour-overlay' && targetId !== 'tour-back-button' && targetId !== 'tour-next-button' && targetId !== 'tour-message' && targetId !== 'tour-content' && targetId !== 'tour-footer' && targetId !== 'tour-emphasis' && targetId !== 'tour-cancel' && targetId !== 'tour-cancel-button'; 

            console.log(targetId, previousStepData.looseSelectionId, condition);

            if (condition) {
                setLoading(true);
                setClickBoundingData(null);
                clickTimerRef.current && window.clearInterval(clickTimerRef.current);

                return (pre || 0) + 1;
            } else
                return pre;
        })
    }, [])

    function trigger(steps: Step[]) {
        stepsRef.current = steps;
        setSteps(steps);
        setCurrentStep(0);
        document.addEventListener('click', listenerFunction)
    }

    function handleBackClick() {
        setLoading(true); 
        setCurrentStep(pre => (pre || 0) - 1);
    }

    function handleNextClick() {
        currentClickElement ? currentClickElement.click() : currentElement?.click();
    }

    function handleCancel() {
        stepsRef.current = null;

        setSteps(null);
        setError(false);
        setCurrentStep(null);
        document.removeEventListener('click', listenerFunction);
    }

    function findElement(id: string | undefined, clickElementId: string | undefined, postAction?: (element: HTMLElement, clickElement?: HTMLElement | null) => void, retries = 0) {
        if (retries >= 4) {
            setLoading(false);
            setError(true);
            return;
        }

        if (!id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setTimeout(() => {
            const element = document.getElementById(id);
            const clickElement = clickElementId ? document.getElementById(clickElementId) : undefined;
            if (element) {
                postAction && postAction(element, clickElement);
                findElement(undefined, undefined, postAction || (() => {}), retries);
            } else {
                console.log(id);
                findElement(id, clickElementId, postAction || (() => {}), retries + 1);
            }
        }, 1000)
    }

    useEffect(() => {
        if (steps && currentStep !== null) {
            if (currentStep === steps.length) {
                handleCancel();
                return;
            }

            const selector = steps[currentStep].selector;
            const clickSelector = steps[currentStep].clickSelector;

            setClickBoundingData(null);
            findElement(selector, clickSelector, (el, clickEl) => {
                if (clickEl) {
                    setCurrentClickElement(clickEl);
                    clickEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    clickTimerRef.current = window.setInterval(() => setClickIconState(pre => !pre), 500)

                    setTimeout(() => {
                        setCurrentElement(el);
                        setCurrentBoundingData(el.getBoundingClientRect());
                        setClickBoundingData(clickEl.getBoundingClientRect());
                    }, 500);
                } else {
                    setCurrentElement(el);
                    setCurrentBoundingData(el.getBoundingClientRect());
                }

                if (steps?.[currentStep || 0].scrollMotion) {
                    setTimeout(() => el.scroll({ top: 0, behavior: 'smooth' }), 500);
                    setTimeout(() => el.scroll({ top: 200, behavior: 'smooth' }), 100);
                }
            });
        }
    }, [currentStep])

    const context = {
        trigger: trigger
    }

    return (
        <TourContext.Provider value={context}>
            {currentStep !== null && steps !== null && steps[currentStep]
            ? 
                <div ref={overlayRef}> 
                    {error && 
                    <div id='tour-cancel' className={styles.tourerror} onClick={handleCancel}>
                        <div id='tour-cancel' style={{ textAlign: 'center' }}>
                            مشکلی در اجرای مراحل آموزش پیش آمده. دوباره تلاش کنید
                        </div>

                        <Pill id='tour-cancel' selected label='بستن' />
                    </div>}

                    {currentStep !== null && !error &&
                    <>
                        {!loading && !steps[currentStep].withoutChevron &&
                        <div 
                            id='tour-chevron' 
                            className={styles.tourchevron}
                            style={{ 
                                top: `${(currentBoundingData?.top || 0) - 22}px`,
                                left: `${(currentBoundingData?.left || 0) + (currentBoundingData?.width || 0) / 2 + 20}px`
                            }}
                        />}

                        <div 
                            id='tour-content' 
                            className={styles.tourcontent}
                            style={{ 
                                textAlign: 'center',
                                paddingInline: '8px',
                                width: `${steps[currentStep].width || 200}px`,
                                height: `${steps[currentStep].height || 100}px`, 
                                transform: !currentBoundingData ? 'translte(-50%, -50%)' : '',
                                top: !currentBoundingData ? '50%' : `${(currentBoundingData?.top || 0) - (steps[currentStep].height || 100) - 10 + (steps[currentStep].topOffset || 0)}px`, 
                                left: !currentBoundingData ? '50%' : `${(currentBoundingData?.left || 0) + (currentBoundingData?.width || 0) / 2 + (steps[currentStep].leftOffset || 0)}px`,
                            }}
                        >
                            <div id='tour-message' style={{ marginBottom: (currentStep && !loading) || (!loading && steps[currentStep].viewOnly) ? '12px' : '' }}>
                                {loading ? 'در حال یافتن مرحله بعدی' : steps[currentStep].message}
                            </div>

                            <div id='tour-footer' style={{ display: 'flex', justifyContent: 'space-between', width: '80%' }}>
                                {!loading &&
                                <Pill id='tour-cancel-button' selected label='لغو' onClick={handleCancel}  
                                    lableStyle={{ fontSize: '14px' }} style={{ background: 'linear-gradient(red 5%, rgb(211, 84, 63) 90%)', paddingBlock: '4px', paddingInline: '6px' }} />} 

                                <div style={{ display: 'flex', justifyContent: 'flex-end', columnGap: '4px' }}>
                                    {currentStep && !loading && !steps[currentStep].withoutPreviousButton
                                    ? 
                                        <Pill id='tour-back-button' label='قبلی' onClick={handleBackClick}  
                                                lableStyle={{ fontSize: '14px' }} style={{ paddingBlock: '4px', paddingInline: '6px' }} /> 
                                    : 
                                        <div/>
                                    }

                                    {steps[currentStep]?.viewOnly && !loading 
                                    ?
                                        <Pill id='tour-next-button' label='بعدی' onClick={handleNextClick} selected icon={arrowIcon}
                                                iconStyle={{ height: '10px' }} lableStyle={{ fontSize: '14px' }} style={{ paddingBlock: '4px', paddingInline: '6px' }} />
                                    :
                                        <div />
                                    }
                                </div>
                            </div>
                        </div>
                    </>}

                    {clickBoundingData &&
                    <img 
                        height={32} width={32}
                        src={clickIconState ? clickIcon : filledClickIcon} 
                        style={{ 
                            zIndex: '1000000',
                            position: 'absolute', 
                            pointerEvents: 'none',
                            left: `${clickBoundingData.left + (clickBoundingData.width / 2)}px`, 
                            top: `${(clickBoundingData.top + (clickBoundingData.height / 2))}px`
                        }}
                    />}

                    <div 
                        id='tour-overlay'
                        className={styles.touroverlay}
                        style={{ clipPath: currentBoundingData && !loading ? `polygon(0 0, 0 100%, 100% 100%, 100% 0, 0 0, ${currentBoundingData.left - 6}px ${currentBoundingData.top - 6}px, ${currentBoundingData.right + 6}px ${currentBoundingData.top - 6}px, ${currentBoundingData.right + 6}px ${currentBoundingData.bottom + 6}px, ${currentBoundingData.left - 6}px ${currentBoundingData.bottom + 6}px, ${currentBoundingData.left - 6}px ${currentBoundingData.top - 6}px)` : 'polygon(0 0, 0 100%, 100% 100%, 100% 0)'}}
                    />

                    {currentBoundingData && !loading &&
                    <div 
                        id='tour-emphasis'
                        onClick={handleNextClick}
                        style={{
                            pointerEvents: steps[currentStep]?.viewOnly ? 'fill' : 'none',
                            position: 'absolute',
                            top: `${currentBoundingData.top - 10 + 4.1}px`,
                            left: `${currentBoundingData.left - 10 + 3.4}px`,
                            right: `${currentBoundingData.right}px`,
                            bottom: `${currentBoundingData.bottom}px`,
                            height: `${currentBoundingData.height + 7}px`,
                            width: `${currentBoundingData.width + 7.9}px`,
                            border: `3px solid ${clickIconState ? '#00000000' : '#00000034'}`,
                            zIndex: '1000000',
                            transition: '0.5s border'
                        }}
                    />}
                </div>
            :
                <></>
            }

            {children}
        </TourContext.Provider>
    )
}

export default TourContext;